import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flow" }
const _hoisted_2 = ["action"]

import { inject, onMounted } from 'vue';
import { useStore } from 'vuex';
import type { TrackingData } from '@src/view_models/TrackingData';
import type { Country } from '@src/view_models/Country';
import type { AddressValidation } from '@src/view_models/Validation';
import type { Salutation } from '@src/view_models/Salutation';
import type { CampaignValues } from '@src/view_models/CampaignValues';
import PaymentSection from '@src/components/pages/donation_form/Payment/PaymentSection.vue';
import PersonalDataSection from '@src/components/pages/donation_form/PersonalData/PersonalDataSection.vue';
import PaymentTextFormButton from '@src/components/shared/form_elements/PaymentTextFormButton.vue';
import SubmitValues from '@src/components/pages/donation_form/SubmitValues.vue';
import ErrorSummary from '@src/components/pages/donation_form/Summaries/ErrorSummary.vue';
import { useDonationFormSubmitHandler } from '@src/components/pages/donation_form/composables/useDonationFormSubmitHandler';
import { QUERY_STRING_INJECTION_KEY } from '@src/util/createCampaignQueryString';
import { usePaymentFunctions } from '@src/components/pages/donation_form/composables/usePaymentFunctions';
import { useAddressSummary } from '@src/components/pages/donation_form/composables/useAddressSummary';
import { useAddressTypeFunctions } from '@src/components/shared/composables/useAddressTypeFunctions';
import { trackDynamicForm } from '@src/util/tracking';
import { useReceiptModel } from '@src/components/pages/donation_form/composables/useReceiptModel';
import { useBankDataSummary } from '@src/components/pages/donation_form/composables/useBankDataSummary';
import PaymentSummarySection from '@src/components/shared/PaymentSummarySection.vue';
import ContentCard from '@src/components/patterns/ContentCard.vue';
import AddressSummary from '@src/components/pages/donation_form/Summaries/AddressSummary.vue';
import DonationSummaryHeadline from '@src/components/pages/donation_form/Summaries/DonationSummaryHeadline.vue';
import Summary from '@src/components/patterns/Summary.vue';
import AccordionItem from '@src/components/patterns/AccordionItem.vue';
import Accordion from '@src/components/patterns/Accordion.vue';

interface Props {
	assetsPath: string;
	validateAddressUrl: string;
	validateEmailUrl: string;
	validateBankDataUrl: string;
	validateLegacyBankDataUrl: string;
	paymentAmounts: number[];
	paymentIntervals: number[];
	paymentTypes: string[];
	countries: Country[];
	salutations: Salutation[];
	trackingData: TrackingData;
	campaignValues: CampaignValues;
	addressValidationPatterns: AddressValidation;
}

export default /*@__PURE__*/_defineComponent({
  ...{
	name: 'DonationForm',
},
  __name: 'DonationForm',
  props: {
    assetsPath: {},
    validateAddressUrl: {},
    validateEmailUrl: {},
    validateBankDataUrl: {},
    validateLegacyBankDataUrl: {},
    paymentAmounts: {},
    paymentIntervals: {},
    paymentTypes: {},
    countries: {},
    salutations: {},
    trackingData: {},
    campaignValues: {},
    addressValidationPatterns: {}
  },
  setup(__props: any) {



const props = __props;

const store = useStore();
const { isDirectDebitPayment, paymentSummary } = usePaymentFunctions( store );
const { addressSummary } = useAddressSummary( store );
const { bankDataSummary } = useBankDataSummary( store );
const { disabledAddressTypes, addressType, addressTypeIsInvalid } = useAddressTypeFunctions( store );
const receiptModel = useReceiptModel( store );

const campaignParams = inject<string>( QUERY_STRING_INJECTION_KEY, '' );

const { submit, submitValuesForm, showErrorSummary } = useDonationFormSubmitHandler(
	store,
	isDirectDebitPayment,
	props.validateAddressUrl,
	props.validateEmailUrl,
	receiptModel.receiptNeeded
);

onMounted( () => {
	trackDynamicForm();
} );


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(PaymentSection, {
      "payment-amounts": __props.paymentAmounts,
      "payment-intervals": __props.paymentIntervals,
      "payment-types": __props.paymentTypes,
      "is-direct-debit-payment": _unref(isDirectDebitPayment)
    }, {
      "error-summary": _withCtx(() => [
        _createVNode(ErrorSummary, {
          "show-error-summary": _unref(showErrorSummary),
          "address-type": _unref(addressType),
          "receipt-model": _unref(receiptModel)
        }, null, 8, ["show-error-summary", "address-type", "receipt-model"])
      ]),
      _: 1
    }, 8, ["payment-amounts", "payment-intervals", "payment-types", "is-direct-debit-payment"]),
    _createVNode(PersonalDataSection, {
      countries: __props.countries,
      salutations: __props.salutations,
      "tracking-data": __props.trackingData,
      "campaign-values": __props.campaignValues,
      "address-validation-patterns": __props.addressValidationPatterns,
      "is-direct-debit-payment": _unref(isDirectDebitPayment),
      "disabled-address-types": _unref(disabledAddressTypes),
      "address-type": _unref(addressType),
      "address-type-is-invalid": _unref(addressTypeIsInvalid),
      "receipt-model": _unref(receiptModel)
    }, null, 8, ["countries", "salutations", "tracking-data", "campaign-values", "address-validation-patterns", "is-direct-debit-payment", "disabled-address-types", "address-type", "address-type-is-invalid", "receipt-model"]),
    (_unref(paymentSummary))
      ? (_openBlock(), _createBlock(ContentCard, {
          key: 0,
          "is-collapsable": true
        }, {
          content: _withCtx(() => [
            _createVNode(Accordion, null, {
              default: _withCtx(() => [
                _createVNode(AccordionItem, null, {
                  title: _withCtx(() => [
                    _createElementVNode("h2", null, _toDisplayString(_ctx.$t( 'donation_form_summary_title' )), 1)
                  ]),
                  content: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                      _createVNode(DonationSummaryHeadline, { payment: _unref(paymentSummary) }, null, 8, ["payment"]),
                      (_unref(addressSummary) || _unref(bankDataSummary))
                        ? (_openBlock(), _createBlock(Summary, { key: 0 }, _createSlots({ _: 2 }, [
                            (_unref(addressSummary))
                              ? {
                                  name: "left",
                                  fn: _withCtx(() => [
                                    _createVNode(AddressSummary, {
                                      address: _unref(addressSummary),
                                      countries: __props.countries,
                                      salutations: __props.salutations,
                                      receiptNeeded: _unref(receiptModel).receiptNeeded.value
                                    }, null, 8, ["address", "countries", "salutations", "receiptNeeded"])
                                  ]),
                                  key: "0"
                                }
                              : undefined,
                            (_unref(bankDataSummary))
                              ? {
                                  name: "right",
                                  fn: _withCtx(() => [
                                    _createVNode(PaymentSummarySection, { "bank-data": _unref(bankDataSummary) }, null, 8, ["bank-data"])
                                  ]),
                                  key: "1"
                                }
                              : undefined
                          ]), 1024))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", null, [
      _createVNode(PaymentTextFormButton, {
        id: "submit-btn",
        "is-loading": _unref(store).getters.isValidating,
        "payment-type": _unref(paymentSummary)?.paymentType,
        onClick: _unref(submit)
      }, null, 8, ["is-loading", "payment-type", "onClick"])
    ]),
    _createElementVNode("form", {
      action: `/donation/add?${_unref(campaignParams)}`,
      method: "post",
      ref_key: "submitValuesForm",
      ref: submitValuesForm,
      id: "submit-form",
      class: "visually-hidden",
      "aria-hidden": "true"
    }, [
      _createVNode(SubmitValues, {
        "tracking-data": __props.trackingData,
        "campaign-values": __props.campaignValues
      }, null, 8, ["tracking-data", "campaign-values"])
    ], 8, _hoisted_2)
  ], 64))
}
}

})